import React, { useEffect, useState } from "react";
import {
  getCameraService,
  CameraType,
  CameraQuality,
  CameraResolution,
  CameraFacingMode,
  CameraLayout,
} from "../../services/cameraService";
import debugx from "debugx";

const debug = debugx("bikedash:camera-control");

interface CameraControlViewProps {
  onClose: () => void;
  onShowConsoleChange?: (show: boolean) => void;
  onLogsChange?: (logs: string[]) => void;
}

export const CameraControlView: React.FC<CameraControlViewProps> = ({
  onClose,
  onShowConsoleChange,
  onLogsChange,
}) => {
  const cameraService = getCameraService();
  const [settings, setSettings] = useState(cameraService.getSettings());
  const [isConnected, setIsConnected] = useState(cameraService.isConnected());
  const [lastError, setLastError] = useState<string | null>(null);
  const [logs, setLogs] = useState<string[]>([]);
  const [showConsole, setShowConsole] = useState(false);
  const logsEndRef = React.useRef<HTMLDivElement>(null);

  useEffect(() => {
    // Subscribe to logs
    const unsubscribeLogs = cameraService.subscribeToLogs((message) => {
      setLogs((prev) => {
        const newLogs = [...prev, message];
        // Keep only last 50 messages
        return newLogs.slice(-50);
      });
    });

    // Poll connection status and errors
    const interval = setInterval(() => {
      setIsConnected(cameraService.isConnected());
      setLastError(cameraService.getLastError());
    }, 500);

    return () => {
      unsubscribeLogs();
      clearInterval(interval);
    };
  }, []);

  useEffect(() => {
    // Auto-scroll logs to bottom
    logsEndRef.current?.scrollIntoView({ behavior: "smooth" });
    // Notify parent of logs change
    onLogsChange?.(logs);
  }, [logs, onLogsChange]);

  const handleCameraSelect = async (
    type: CameraType | "off",
    facingMode?: CameraFacingMode,
  ) => {
    debug("Camera selected: type=%s, facingMode=%s", type, facingMode);

    if (type === "off") {
      // Turn off camera
      await cameraService.updateSettings({ enabled: false });
      await cameraService.stop();
    } else {
      // Enable and configure camera
      if (type === "phone" && facingMode) {
        await cameraService.updateSettings({ type, facingMode, enabled: true });
      } else {
        await cameraService.updateSettings({ type, enabled: true });
      }
      // Start the camera
      await cameraService.start();
    }

    setSettings(cameraService.getSettings());
  };

  const handleQualityChange = async (quality: CameraQuality) => {
    debug("Camera quality changed to: %s", quality);
    await cameraService.updateSettings({ quality });
    setSettings(cameraService.getSettings());
  };

  const handleResolutionChange = async (resolution: CameraResolution) => {
    debug("Camera resolution changed to: %s", resolution);
    await cameraService.updateSettings({ resolution });
    setSettings(cameraService.getSettings());
  };

  const handleConnect = async () => {
    if (settings.type === "bluetooth") {
      // Clear previous error
      cameraService.clearError();
      setLastError(null);

      await cameraService.connectBluetooth();
      setIsConnected(cameraService.isConnected());
    }
  };

  const handleDisconnect = async () => {
    if (settings.type === "bluetooth") {
      await cameraService.disconnectBluetooth();
      setIsConnected(false);
    }
  };

  const handleLayoutChange = async (layout: CameraLayout) => {
    debug("Camera layout changed to: %s", layout);
    await cameraService.updateSettings({ layout });
    setSettings(cameraService.getSettings());
  };

  return (
    <>
      <div
        className="island-header"
        style={{
          marginBottom: "0",
          padding: "8px",
          gap: "6px",
        }}
      >
        <div className="island-icon">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
            <path
              d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"
              stroke={lastError ? "#ef4444" : "#60a5fa"}
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              fill="none"
            />
            <circle
              cx="12"
              cy="13"
              r="4"
              stroke={lastError ? "#ef4444" : "#60a5fa"}
              strokeWidth="2"
              fill="none"
            />
          </svg>
        </div>

        {/* Camera source toggle buttons */}
        <div
          style={{
            display: "flex",
            gap: "2px",
            background: "rgba(255, 255, 255, 0.1)",
            borderRadius: "6px",
            padding: "2px",
            flex: 1,
          }}
        >
          <button
            onClick={() => handleCameraSelect("off")}
            style={{
              flex: 1,
              padding: "6px 8px",
              fontSize: "11px",
              background: !settings.enabled ? "var(--accent)" : "transparent",
              border: !settings.enabled ? "1px solid var(--accent)" : "none",
              borderRadius: "4px",
              color: !settings.enabled ? "white" : "var(--text-secondary)",
              cursor: "pointer",
              fontWeight: !settings.enabled ? 600 : 400,
              transition: "all 0.2s",
            }}
          >
            ⚫ OFF
          </button>
          <button
            onClick={() => handleCameraSelect("bluetooth")}
            style={{
              flex: 1,
              padding: "6px 8px",
              fontSize: "11px",
              background:
                settings.enabled && settings.type === "bluetooth"
                  ? "var(--accent)"
                  : "transparent",
              border:
                settings.enabled && settings.type === "bluetooth"
                  ? "1px solid var(--accent)"
                  : "none",
              borderRadius: "4px",
              color:
                settings.enabled && settings.type === "bluetooth"
                  ? "white"
                  : "var(--text-secondary)",
              cursor: "pointer",
              fontWeight:
                settings.enabled && settings.type === "bluetooth" ? 600 : 400,
              transition: "all 0.2s",
            }}
          >
            🔵 BT
          </button>
          <button
            onClick={() => handleCameraSelect("phone", "user")}
            style={{
              flex: 1,
              padding: "6px 8px",
              fontSize: "11px",
              background:
                settings.enabled &&
                settings.type === "phone" &&
                settings.facingMode === "user"
                  ? "var(--accent)"
                  : "transparent",
              border:
                settings.enabled &&
                settings.type === "phone" &&
                settings.facingMode === "user"
                  ? "1px solid var(--accent)"
                  : "none",
              borderRadius: "4px",
              color:
                settings.enabled &&
                settings.type === "phone" &&
                settings.facingMode === "user"
                  ? "white"
                  : "var(--text-secondary)",
              cursor: "pointer",
              fontWeight:
                settings.enabled &&
                settings.type === "phone" &&
                settings.facingMode === "user"
                  ? 600
                  : 400,
              transition: "all 0.2s",
            }}
          >
            🤳 Front
          </button>
          <button
            onClick={() => handleCameraSelect("phone", "environment")}
            style={{
              flex: 1,
              padding: "6px 8px",
              fontSize: "11px",
              background:
                settings.enabled &&
                settings.type === "phone" &&
                settings.facingMode === "environment"
                  ? "var(--accent)"
                  : "transparent",
              border:
                settings.enabled &&
                settings.type === "phone" &&
                settings.facingMode === "environment"
                  ? "1px solid var(--accent)"
                  : "none",
              borderRadius: "4px",
              color:
                settings.enabled &&
                settings.type === "phone" &&
                settings.facingMode === "environment"
                  ? "white"
                  : "var(--text-secondary)",
              cursor: "pointer",
              fontWeight:
                settings.enabled &&
                settings.type === "phone" &&
                settings.facingMode === "environment"
                  ? 600
                  : 400,
              transition: "all 0.2s",
            }}
          >
            📱 Back
          </button>
        </div>

        {/* Layout toggles */}
        <div
          style={{
            display: "flex",
            gap: "2px",
            background: "rgba(255, 255, 255, 0.1)",
            borderRadius: "6px",
            padding: "2px",
            flexShrink: 0,
          }}
        >
          <button
            onClick={() => handleLayoutChange("regular")}
            style={{
              padding: "6px 8px",
              fontSize: "11px",
              background:
                settings.layout === "regular" ? "var(--accent)" : "transparent",
              border:
                settings.layout === "regular"
                  ? "1px solid var(--accent)"
                  : "none",
              borderRadius: "4px",
              color:
                settings.layout === "regular"
                  ? "white"
                  : "var(--text-secondary)",
              cursor: "pointer",
              fontWeight: settings.layout === "regular" ? 600 : 400,
              transition: "all 0.2s",
            }}
          >
            📐
          </button>
          <button
            onClick={() => handleLayoutChange("ultrawide")}
            style={{
              padding: "6px 8px",
              fontSize: "11px",
              background:
                settings.layout === "ultrawide"
                  ? "var(--accent)"
                  : "transparent",
              border:
                settings.layout === "ultrawide"
                  ? "1px solid var(--accent)"
                  : "none",
              borderRadius: "4px",
              color:
                settings.layout === "ultrawide"
                  ? "white"
                  : "var(--text-secondary)",
              cursor: "pointer",
              fontWeight: settings.layout === "ultrawide" ? 600 : 400,
              transition: "all 0.2s",
            }}
          >
            🎞️
          </button>
          <button
            onClick={() => handleLayoutChange("full")}
            style={{
              padding: "6px 8px",
              fontSize: "11px",
              background:
                settings.layout === "full" ? "var(--accent)" : "transparent",
              border:
                settings.layout === "full" ? "1px solid var(--accent)" : "none",
              borderRadius: "4px",
              color:
                settings.layout === "full" ? "white" : "var(--text-secondary)",
              cursor: "pointer",
              fontWeight: settings.layout === "full" ? 600 : 400,
              transition: "all 0.2s",
            }}
          >
            ⛶
          </button>
          <button
            onClick={() => handleLayoutChange("full-wide")}
            style={{
              padding: "6px 8px",
              fontSize: "11px",
              background:
                settings.layout === "full-wide"
                  ? "var(--accent)"
                  : "transparent",
              border:
                settings.layout === "full-wide"
                  ? "1px solid var(--accent)"
                  : "none",
              borderRadius: "4px",
              color:
                settings.layout === "full-wide"
                  ? "white"
                  : "var(--text-secondary)",
              cursor: "pointer",
              fontWeight: settings.layout === "full-wide" ? 600 : 400,
              transition: "all 0.2s",
            }}
          >
            ⬚
          </button>
        </div>

        <button
          className="island-close-btn"
          onClick={onClose}
          style={{
            background: "none",
            border: "none",
            color: "var(--text-secondary)",
            fontSize: "18px",
            cursor: "pointer",
            padding: "2px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          ×
        </button>
      </div>

      {/* Content wrapper */}
      <div
        className="island-details"
        style={{
          padding: "10px",
          paddingTop: "0px",
          paddingBottom: "0px",
          display: "flex",
          flexDirection: "column",
          height: "calc(100% - 50px)", // Account for header height
          overflow: "hidden",
        }}
      >
        {/* Scrollable content area */}
        <div
          style={{
            paddingTop: "4px",
            paddingBottom: "4px",
            flex: 1,
            overflowY: "auto",
            overflowX: "hidden",
            WebkitOverflowScrolling: "touch", // For smooth scrolling on iOS
          }}
        >
          {!settings.enabled ? (
            <>
              {/* Camera OFF state */}
              <div style={{ marginBottom: "12px", textAlign: "center" }}>
                <div
                  style={{
                    fontSize: "11px",
                    color: "var(--text-secondary)",
                    marginBottom: "6px",
                  }}
                >
                  Camera is off. Select a camera source above to start.
                </div>
              </div>
            </>
          ) : settings.type === "bluetooth" ? (
            <>
              {/* Connection Status & Control */}
              <div
                style={{
                  marginBottom: "12px",
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                }}
              >
                <div
                  style={{
                    fontSize: "11px",
                    color: lastError ? "#ef4444" : "var(--text-secondary)",
                    whiteSpace: "nowrap",
                  }}
                >
                  {isConnected
                    ? "✓ Connected"
                    : lastError
                      ? `✗ ${lastError}`
                      : "Disconnected"}
                </div>
                <button
                  onClick={isConnected ? handleDisconnect : handleConnect}
                  className={`island-btn ${isConnected ? "danger" : "primary"}`}
                  style={{ flex: 1, padding: "8px", fontSize: "11px" }}
                >
                  {isConnected ? "Disconnect" : "Connect"}
                </button>
                <button
                  onClick={() => {
                    const newValue = !showConsole;
                    setShowConsole(newValue);
                    onShowConsoleChange?.(newValue);
                  }}
                  className={`island-btn ${showConsole ? "primary" : "default"}`}
                  style={{
                    padding: "8px",
                    fontSize: "11px",
                    whiteSpace: "nowrap",
                  }}
                >
                  💻
                </button>
              </div>

              {/* Resolution (BT only) */}
              <div
                style={{
                  marginBottom: "12px",
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                }}
              >
                <div
                  style={{
                    fontSize: "11px",
                    color: "var(--text-secondary)",
                    whiteSpace: "nowrap",
                  }}
                >
                  Resolution
                </div>
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(5, 1fr)",
                    gap: "4px",
                    flex: 1,
                  }}
                >
                  {(["QQVGA", "QVGA", "HQVGA", "CIF", "VGA"] as const).map(
                    (res) => (
                      <button
                        key={res}
                        onClick={() => handleResolutionChange(res)}
                        className={`island-btn ${settings.resolution === res ? "primary" : "default"}`}
                        style={{ padding: "6px 4px", fontSize: "9px" }}
                      >
                        {res}
                      </button>
                    ),
                  )}
                </div>
              </div>

              {/* Quality (BT only) */}
              <div
                style={{
                  marginBottom: "12px",
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                }}
              >
                <div
                  style={{
                    fontSize: "11px",
                    color: "var(--text-secondary)",
                    whiteSpace: "nowrap",
                  }}
                >
                  Quality
                </div>
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(5, 1fr)",
                    gap: "4px",
                    flex: 1,
                  }}
                >
                  {(["High", "Medium", "Low", "Blocky", "Trash"] as const).map(
                    (qual) => (
                      <button
                        key={qual}
                        onClick={() => handleQualityChange(qual)}
                        className={`island-btn ${settings.quality === qual ? "primary" : "default"}`}
                        style={{ padding: "6px 4px", fontSize: "9px" }}
                      >
                        {qual}
                      </button>
                    ),
                  )}
                </div>
              </div>
            </>
          ) : (
            <>
              {/* Phone camera info */}
              <div style={{ marginBottom: "12px" }}>
                <div
                  style={{
                    fontSize: "11px",
                    color: "var(--text-secondary)",
                    marginBottom: "6px",
                  }}
                >
                  Status: {isConnected ? "✓ Camera Active" : "Camera Inactive"}
                </div>
                <div
                  style={{
                    fontSize: "10px",
                    color: "var(--text-secondary)",
                    opacity: 0.7,
                  }}
                >
                  Using {settings.facingMode === "user" ? "front" : "rear"}{" "}
                  camera. Switch cameras using the tabs above.
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </>
  );
};
